import { combineLatest, Observable, timer } from 'rxjs';
import { AccountService } from '../services/account.service';
import { ContractService } from '../services/contract.service';
import { EthWalletService } from '../services/eth-wallet.service';
import { Token } from './token';
import { map, mergeMap } from 'rxjs/operators';
import { distinctBigNumberUntilChanged, sharePublishReplay, swalloError } from 'ngx-libs';
import ZTokenAbi from 'src/app/abi/z-token';
import BigNumber from 'bignumber.js';

export class ZToken extends Token {
  aToken: Observable<Token>;
  aTokenBalance: Observable<BigNumber>;
  // ztoken / underlying
  zuPrice: Observable<BigNumber>;
  interest: Observable<BigNumber>;
  collected: Observable<BigNumber>;
  info: Observable<any>;

  constructor(
    public contract: string,
    public decimals: number,
    protected accountService: AccountService,
    protected ethWalletService: EthWalletService,
    protected contractService: ContractService
  ) {
    super(contract, decimals, accountService, ethWalletService, contractService, ZTokenAbi);

    this.aToken = this.getAToken().pipe(
      map((address) => new Token(address, this.decimals, this.accountService, this.ethWalletService, this.contractService)),
      sharePublishReplay(1)
    );

    this.aTokenBalance = combineLatest([this.aToken, timer(0, 5000)]).pipe(
      mergeMap(([aToken]) => aToken.balanceOf(this.contract).pipe(swalloError())),
      distinctBigNumberUntilChanged(),
      sharePublishReplay(1)
    );

    this.zuPrice = combineLatest([this.totalSupply, this.aTokenBalance]).pipe(
      map(([totalSupply, aTokenBalance]) => aTokenBalance.div(totalSupply)),
      distinctBigNumberUntilChanged(),
      sharePublishReplay(1)
    );

    this.interest = timer(0, 5000).pipe(
      mergeMap(() => this.getInterest().pipe(swalloError())),
      sharePublishReplay(1)
    );

    this.collected = timer(0, 5000).pipe(
      mergeMap(() => this.getCollected().pipe(swalloError())),
      sharePublishReplay(1)
    );

    this.info = timer(0, 5000).pipe(
      mergeMap(() => this.getInfo().pipe(swalloError())),
      sharePublishReplay(1)
    );
  }

  getAToken() {
    return this.call((i) => i.methods.aToken());
  }

  unwrap(amount: any) {
    return this.send((i) => i.methods.unwrap(new BigNumber(amount).times(this.decimalsFactor).toString(10)));
  }

  getInterest() {
    return this.call((i) => i.methods.interest()).pipe(map((v) => new BigNumber(v).div(this.decimalsFactor)));
  }

  getCollected() {
    return this.call((i) => i.methods.collected()).pipe(map((v) => new BigNumber(v).div(this.decimalsFactor)));
  }

  getInfo() {
    return this.call((i) => i.methods.info()).pipe(map((v) => {
      return {
        availableBorrowsBase: new BigNumber(v.availableBorrowsBase).div(10**8),
        healthFactor: new BigNumber(v.healthFactor).fromDecimal(),
        totalCollateralBase: new BigNumber(v.totalCollateralBase).div(10**8),
        totalDebtBase: new BigNumber(v.totalDebtBase).div(10**8),
        borrowingABalance: new BigNumber(v._borrowingABalance).fromDecimal(),
        debt: new BigNumber(v._debt).fromDecimal(),
        ltv: new BigNumber(v._ltv).div(10000),
        currentLiquidationThreshold: new BigNumber(v.currentLiquidationThreshold).div(10000)
      }
    }));
  }

  collectInterest() {
    return this.send((i) => i.methods.collectInterest());
  }

  collectRewards(assets: string[]) {
    return this.send((i) => i.methods.collectRewards(assets));
  }
}
